import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as utils from '@/utils';
/**
 * A component for a button that will display some controls in a popover when clicked.
 *
 * The button can have a popover on hover describing the controls that will be
 * shown when the button is clicked, which will never be visible when the
 * popover containing the controls is shown.
 */
export default tsx
    .componentFactoryOf()
    .create({
    name: 'button-with-popover-controls',
    props: {
        /** The id to set on the button. If not given a random unique id will be generated. */
        id: p(String).optional,
        /**
         * Description of the controls.
         *
         * Will be shown in a popover when the button is hovered, and also
         * set as the `aria-label` on the button.
         */
        description: p(String).required,
        /** Props for the popover component. */
        popoverProps: p.ofType().required,
    },
    /** @returns The initial state of this component. */
    data() {
        return {
            buttonId: this.id ?? `button-with-popover-controls-${utils.getUniqueId()}`,
            controlsVisible: false,
        };
    },
    computed: {
        /**
         * @returns The popover containing the description. Never rendered when the controls are visible.
         */
        descriptionPopover() {
            return utils.ifOrEmpty(!this.controlsVisible, () => (<b-popover target={this.buttonId} triggers="hover focus" container={this.popoverProps.container} placement={this.popoverProps.placement} boundary={this.popoverProps.boundary} boundaryPadding={this.popoverProps.boundaryPadding}>
                        {this.description}
                    </b-popover>));
        },
        /** @returns The popover containing controls. */
        controlsPopover() {
            return (<b-popover target={this.buttonId} id={this.popoverProps.id} triggers={this.popoverProps.triggers ?? 'click blur'} title={this.popoverProps.title} placement={this.popoverProps.placement} container={this.popoverProps.container} boundary={this.popoverProps.boundary} boundaryPadding={this.popoverProps.boundaryPadding} customClass={this.popoverProps.customClass} onShow={() => {
                    this.controlsVisible = true;
                }} onHide={() => {
                    this.controlsVisible = false;
                }} onShown={() => tsx.emitOn(this, 'onControlsShown')}>
                        {utils.renderScopedSlot(undefined, this.$scopedSlots.controlsContent, () => {
                    throw new Error('No `controlsContent` slot was provided.');
                })}
                    </b-popover>);
        },
    },
    /** @returns The rendered component. */
    render() {
        return (<b-button id={this.buttonId} aria-label={this.description}>
                    {utils.renderScopedSlot(undefined, this.$scopedSlots.buttonContent, () => {
                throw new Error('No `buttonContent` slot was provided.');
            })}

                    {this.descriptionPopover}

                    {this.controlsPopover}
                </b-button>);
    },
});
